import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PreviewDialogProps } from "../types";

export const PreviewDialog = ({
  isOpen,
  onOpenChange,
  formData,
  businessProfile,
  onSubmit,
  isProcessing,
}: PreviewDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Förhandsgranska annons</DialogTitle>
          <DialogDescription>
            Granska din annons innan du skapar den
          </DialogDescription>
        </DialogHeader>

        <div className="py-3 max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold">{formData.roleTitle}</h3>
            <p className="text-sm text-muted-foreground">
              {businessProfile?.business} · {formData.location} ·{" "}
              {formData.formOfEmployment} ·{" "}
              {formData.isRemote ? "Distansarbete möjligt" : "På plats"}
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[50vh]">
            {/* Left column - sticky */}
            <div className="space-y-4 lg:sticky lg:top-0 lg:max-h-[50vh] lg:overflow-y-auto">
              {/* Job Description */}
              <div>
                <h4 className="font-medium mb-2">Jobbeskrivning</h4>
                <div 
                  className="text-sm bg-muted/30 p-3 rounded-lg preview-job-description"
                  dangerouslySetInnerHTML={{ __html: formData.jobDescription }}
                />
              </div>

              {/* Tags */}
              <div>
                <h4 className="font-medium mb-2">Taggar</h4>
                <div className="flex flex-wrap gap-1.5">
                  {formData.selectedTags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              {formData.selectedBenefits.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Förmåner</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {formData.selectedBenefits.map((benefit) => (
                      <span
                        key={benefit}
                        className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Language Skills */}
              <div>
                <h4 className="font-medium mb-2">Språkkunskaper</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Svenska:</span>
                    <p className="font-medium">{formData.swedishLevel}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Engelska:</span>
                    <p className="font-medium">{formData.englishLevel}</p>
                  </div>
                </div>
              </div>

              {/* Salary & Experience */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Lön</h4>
                  <p className="text-sm">{formData.salary || "30 000 kr/mån"}</p>
                  {formData.payTransparency && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Lön visas i annonsen
                    </p>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-sm">Erfarenhet</h4>
                  <p className="text-sm">{formData.experience}</p>
                </div>
              </div>
            </div>

            {/* Right column - scrollable */}
            <div className="lg:max-h-[50vh] lg:overflow-y-auto">
              {/* Qualifying Questions */}
              <div>
                <h4 className="font-medium mb-2">Kvalificerande frågor</h4>
                <div className="space-y-2">
                  {formData.qualifyingQuestions.map((question, index) => (
                    <div
                      key={index}
                      className="p-2 bg-secondary/20 rounded-lg"
                    >
                      <p className="font-medium text-sm mb-1">{question.question}</p>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex items-start gap-1.5">
                          <div className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                            {question.positiveResponse}
                          </div>
                          <span className="text-green-700">
                            {question.onPositiveResponse}
                          </span>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <div className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                            {question.negativeResponse}
                          </div>
                          <span className="text-red-700">
                            {question.onNegativeResponse}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Redigera
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isProcessing}
            className="flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Skapar annons...
              </>
            ) : (
              <>Skapa annons och hitta kandidater</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewDialog; 